import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';

// Session 19: agency_only clients get a small filled star before the name
// (theme accent), instead of the old "agency approval" pill. Other modes: none.
export function AgencyStar() {
  return (
    <span className="agency-star" role="img" aria-label="Agency approval"
          title="Agency approval: you approve everything for this client">
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z" />
      </svg>
    </span>
  );
}

function ClientName({ client, isAgency }) {
  return (
    <span className="switcher-name">
      {isAgency && client?.approval_mode === 'agency_only' && <AgencyStar />}
      <span>{client?.name || 'Select client'}</span>
    </span>
  );
}

export default function ClientSwitcher() {
  const { clients, activeId, setActiveId, isAgency } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);   // Session 24: "Switched to {name}"
  const ref = useRef(null);
  const toastTimer = useRef(null);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function pick(c) {
    const changed = c.client_id !== activeId;
    setActiveId(c.client_id);
    setOpen(false);
    if (changed) {
      clearTimeout(toastTimer.current);
      setToast(c.name);
      toastTimer.current = setTimeout(() => setToast(null), 3500);
    }
  }

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!clients || clients.length === 0) return null;
  const active = clients.find((c) => c.client_id === activeId);

  return (
    <div className="switcher" ref={ref}>
      <div className="switcher-row">
        <button
          type="button"
          className="switcher-btn"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <ClientName client={active} isAgency={isAgency} />
          <span className="chev" aria-hidden="true">▾</span>
        </button>
        {isAgency && active && (
          <button
            type="button"
            className="switcher-gear"
            title={`Settings for ${active.name}`}
            aria-label={`Settings for ${active.name}`}
            onClick={() => { setOpen(false); navigate(clientPath(`/admin/clients/${active.client_id}/settings`, active.client_id)); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
            </svg>
          </button>
        )}
      </div>
      {open && (
        <div className="switcher-menu" role="listbox">
          {clients.map((c) => (
            <button
              key={c.client_id}
              type="button"
              className={c.client_id === activeId ? 'active' : ''}
              onClick={() => pick(c)}
              role="option"
              aria-selected={c.client_id === activeId}
            >
              <ClientName client={c} isAgency={isAgency} />
            </button>
          ))}
          {isAgency && (
            <>
              <div className="divider" role="separator" />
              <button
                type="button"
                className="add"
                onClick={() => { setOpen(false); navigate('/admin/clients/new'); }}
              >
                + Add new client
              </button>
            </>
          )}
        </div>
      )}
      {toast && <div className="toast" role="status" aria-live="polite">Switched to <strong>{toast}</strong></div>}
    </div>
  );
}
